// FOUNDER-SITE-DEFECTS-20261007-001 · the Kit 2.01 exported sign-up's password policy, read from the shared one.
//
// The public home JOIN FREE -> club panel renders the exported design's own sign-up (sg-beta-v2.01.html), not
// SignupFields. Its logic block cannot import, so DesignSurface publishes this object on
// `window.__SG_SIGNUP_POLICY` (the same seam as __SG_MAP_TRANSFORM) and the export reads every verdict from it.
// The rules themselves stay in @repo/ui/lib/signup-funnel: nothing here restates a length or a character class.
import {
  SIGNUP_PASSWORD_RULE,
  confirmMessage,
  emailMessage,
  nameMessage,
  passwordMessage,
  phoneMessage,
} from "@repo/ui/lib/signup-funnel";
import { MEMBER_TYPE_GROUPS } from "./member-types";

export interface DesignSignupCreds {
  first: string;
  last: string;
  email: string;
  phone: string;
  password: string;
  confirm: string;
}

export interface DesignSignupPolicy {
  /** The always-visible requirement line. */
  rule: string;
  /** Why the password is refused, or "" when acceptable or not yet typed (an empty field is not shouted at). */
  passwordMessage(password: string): string;
  /** Why the confirmation is refused, or "" when it matches or nothing has been typed into it. */
  confirmMessage(password: string, confirm: string): string;
  /** Every reason the credentials step cannot be left, in field order. Empty means Continue is open. */
  blockers(c: DesignSignupCreds): string[];
}

export const designSignupPolicy: DesignSignupPolicy = {
  rule: SIGNUP_PASSWORD_RULE,
  passwordMessage: (password) => (password.length === 0 ? "" : (passwordMessage(password) ?? "")),
  confirmMessage: (password, confirm) =>
    password.length === 0 || confirm.length === 0 ? "" : (confirmMessage(password, confirm) ?? ""),
  blockers: (c) =>
    [
      nameMessage("first name", c.first),
      nameMessage("last name", c.last),
      emailMessage(c.email),
      phoneMessage(c.phone),
      passwordMessage(c.password),
      c.password.length === 0 ? null : confirmMessage(c.password, c.confirm),
    ].filter((m): m is string => m !== null),
};

/**
 * The export keeps its ticks as `group:Label` (the label is the founder's wording). The registration contract takes the
 * stable ids from member-types.ts (`group:slug`), so each tick is resolved against that taxonomy by group + label (an
 * already-stable id passes through). A tick the taxonomy does not know is dropped here rather than guessed at; nothing is
 * remapped to a different meaning. The BE still validates against its own copy (see the BE contract note in the packet).
 */
export function memberTypeIdsFromDesign(ticks: readonly string[] | undefined): string[] {
  const out: string[] = [];
  for (const raw of ticks ?? []) {
    const hit = MEMBER_TYPE_GROUPS.flatMap((g) => g.options.map((o) => ({ g, o }))).find(
      ({ g, o }) => o.id === raw || `${g.id}:${o.label}` === raw,
    );
    if (hit && !out.includes(hit.o.id)) out.push(hit.o.id);
  }
  return out;
}

/** What the exported final action posts, as cart-store.signUp's fields. */
export function registrationFromDesign(f: DesignRegisterFields): DesignRegisterFields & { memberTypes: string[] } {
  return { ...f, memberTypes: memberTypeIdsFromDesign(f.memberTypes) };
}

export interface DesignRegisterFields {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  /** `group:Label` ticks from the export. */
  memberTypes?: string[];
}

declare global {
  interface Window {
    __SG_SIGNUP_POLICY?: DesignSignupPolicy;
    /** useCartStore.getState().signUp -> /api/auth/register: resolves {ok:true} only after a real jwt response. */
    __SG_SIGNUP_REGISTER?: (fields: DesignRegisterFields) => Promise<{ ok: boolean; error?: string }>;
    /** Opens the gated sign-up (age step first). The export's every sign-up entry calls it instead of drawing its own personal fields. */
    __SG_SIGNUP_GATE?: () => void;
  }
}
